import React, {Component, ReactElement, ReactNode} from 'react'
import NatAdminUserRepo from './repository/NatAdminUserRepo'
import {NatGetAdminUser} from './repository/NatGetAdminUser'
import NatOwnerCondominiumRepo from '../ownerCondominium/repository/NatOwnerCondominiumRepo'
import {AVAILABLE_OR_NOT, MESSAGE_TYPE, ROLE_FOR_ADMINISTRATOR} from '../../../CodeMaster'
import {formatDateSlashSeparator} from '../../../formatters/formatDate'
import {AdminPagePropsSet, createAdminPage} from '../../../components/shared/hoc/createAdminPage'
import EventEmitterExAdmin from '../../../components/shared/pageBaseComponents/EventEmitterExAdmin'
import {Messages} from '../../../Messages'
import {Nullable} from '../../../app/TypeHelpers'
import {
    PageFooterArea,
    PageFotterAreaLeft,
    PageFotterAreaRight,
    PageMainArea,
} from '../../../components/shared/hoc/withAdminPageTemplate'
import {BackLink, DeleteLink} from '../../../styledComponents/adminPage/Links'
import {Box, Table, TableBody, TableCell, TableRow} from '@mui/material'
import {StyledTableCaption, TableRowName} from '../../../styledComponents/adminPage/Table'
import {HKConstants} from '../../../HKConstants'
import AuthorityChecker from '../../../components/AuthorityChecker'
import {NatGetOwnerCondominium} from '../ownerCondominium/repository/NatGetOwnerCondominium'
import {NatDeleteAdminUser} from './repository/NatDeleteAdminUser'
import {AdminPrimaryButton} from '../../../styledComponents/adminPage/Buttons'
import {HeadingAreaMessageParams} from '../../../components/shared/pageBaseComponents/HeadingAreaMessageComponent'
import {getStringOrEmptyString} from '../../common/util/Natutil'
import {AdminUserRelatedCondominiumGroup} from './repository/AdminUserRelatedCondominiumGroup'
import moment, {Moment} from 'moment'
import NatCurrentDateTimeRepo from '../currentDateTime/repository/NatCurrentDateTimeRepo'
import {formatYearAndMonth} from '../../../formatters/formatMoment'
import {Location} from 'react-router-dom'

interface Props {
    adminId: string
    AdminUserRepo: NatAdminUserRepo
    OwnerCondominiumRepo: NatOwnerCondominiumRepo
    currentDateTimeRepo: NatCurrentDateTimeRepo
    eventEmitter: EventEmitterExAdmin
    location: Location
}

interface States {
    dispItems: Nullable<DispItems>
    isLoadCompleted: boolean
    roleId: string
    currentDateTime: Nullable<Moment>
}

interface DispItems {
    roleName: string
    condominiumGroupName: string
    condominiumCd: Nullable<string>
    loginId: string
    residentUser: Nullable<string>
    mailAddress: string
    name: string
    termStartDate: Nullable<string>
    termEndDate: Nullable<string>
    loginAvailability: AVAILABLE_OR_NOT
    mailAddressPc: Nullable<string>
    mailAddressMb: Nullable<string>
    handoverCondominiumGroupName: Nullable<string>
    endDateOfHandoverPeriod: Nullable<Moment>
}

class NatAdminAdminUserPage extends Component<Props & AdminPagePropsSet, States> {

    constructor(props: Props & AdminPagePropsSet) {
        super(props)

        this.state = {
            isLoadCompleted: false,
            dispItems: null,
            roleId: '',
            currentDateTime: null,
        }
    }

    async componentDidMount(): Promise<void> {
        const {adminId, asyncCallFunction, currentDateTimeRepo, AdminUserRepo} = this.props

        await this.props.eventEmitter.showLoading()
        try {
            const currentDateTime = await currentDateTimeRepo.getCurrentDateTime()
            if (!currentDateTime || !currentDateTime.details) {
                return
            }
            const today = moment(`${currentDateTime.details.currentDateTime}`)

            // 管理者ユーザ取得
            const adminUserResponse: Nullable<NatGetAdminUser> = await this.props.asyncCallFunction(
                this.props.AdminUserRepo.getAdminUser.bind(this.props.AdminUserRepo, this.props.adminId),
                (httpErrorOrNetworkError) => {
                    this.props.eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                },
            )
            if (!adminUserResponse?.details) {
                return
            }

            const adminUser = adminUserResponse.details
            const dispItems: DispItems = {
                roleName: adminUser.roleName,
                condominiumGroupName: adminUser.condominiumGroupName,
                condominiumCd: adminUser.condominiumCd,
                loginId: adminUser.loginId,
                residentUser: '',
                mailAddress: (adminUser.mailAddressPc ?
                    adminUser.mailAddressPc : adminUser.mailAddressMb) ?? '',
                name: getStringOrEmptyString(adminUser.familyName)
                    + ' ' + getStringOrEmptyString(adminUser.firstName),
                termStartDate: adminUser.termStartDate,
                termEndDate: adminUser.termEndDate,
                loginAvailability:
                    AVAILABLE_OR_NOT.codeToEnumConstants(adminUser.loginAvailability),
                mailAddressPc: adminUser.mailAddressPc,
                mailAddressMb: adminUser.mailAddressMb,
                handoverCondominiumGroupName: null,
                endDateOfHandoverPeriod: null,
            }

            if (adminUser.roleId === ROLE_FOR_ADMINISTRATOR.FRONT_STAFF.value) {
                // 引継中物件グループ情報取得
                const subCondominiumGroup: Nullable<AdminUserRelatedCondominiumGroup> = await asyncCallFunction(
                    AdminUserRepo.getAdminUserRelatedCondominiumGroup.bind(AdminUserRepo, adminId),
                    (httpErrorOrNetworkError) => {
                        this.props.eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                    },
                )
                if (subCondominiumGroup) {
                    if (subCondominiumGroup.subCondominiumGroupId &&
                        subCondominiumGroup.subCondominiumGroupName &&
                        subCondominiumGroup.endDateOfSubCondominiumGroup) {

                        const endDate = moment(subCondominiumGroup.endDateOfSubCondominiumGroup)
                        dispItems.handoverCondominiumGroupName = subCondominiumGroup.subCondominiumGroupName
                        dispItems.endDateOfHandoverPeriod = endDate
                    }
                }
            }

            if (adminUser.familyNameKana || adminUser.firstNameKana) {
                dispItems.name = dispItems.name +
                    ' （' + getStringOrEmptyString(adminUser.familyNameKana) +
                    ' ' + getStringOrEmptyString(adminUser.firstNameKana) + '）'
            }

            // ロールが管理組合役員
            // かつ、素敵ネットIDと物件コードが設定されている場合、居住者ユーザ取得
            if (adminUser.roleId === ROLE_FOR_ADMINISTRATOR.ASSOC.value &&
                adminUser.sutekiNetId && adminUser.condominiumCd) {

                const ownerCondominiumInfo: Nullable<NatGetOwnerCondominium> =
                    await this.props.asyncCallFunction(
                        this.props.OwnerCondominiumRepo.getOwnerCondomoinum.bind(
                            this.props.OwnerCondominiumRepo,
                            adminUser.sutekiNetId,
                            adminUser.condominiumCd,
                        ),
                        (httpErrorOrNetworkError) => {
                            this.props.eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                        },
                    )

                if (ownerCondominiumInfo?.details?.roomToResident) {
                    dispItems.residentUser = ownerCondominiumInfo.details.roomToResident
                        .map(userInfo => userInfo.ridgeName + ' ' + userInfo.roomNumber)
                        .join(',')
                }
            }

            this.setState({
                dispItems: dispItems,
                roleId: adminUser.roleId,
                currentDateTime: today,
            })

            this.showInitialMessage()
        } finally {
            this.props.eventEmitter.closeLoading()
            this.setState({isLoadCompleted: true})
        }
    }

    showInitialMessage(): void {
        if (!this.props.location || !this.props.location.state) {
            return
        }
        const initialMessage: HeadingAreaMessageParams = this.props.location.state.initialMessage
        this.props.navigate(this.props.location.pathname, {replace: true})
        this.props.eventEmitter.showHeadingAreaMessage(initialMessage)
    }

    // 管理者ユーザ編集
    editAdminUserButtonClick(adminId: string): void {
        this.props.navigate(`/admin/adminUserEdit/${adminId}`)
    }

    // 管理者ユーザ削除
    async deleteAdminUserLinkClick(): Promise<void> {
        await this.props.eventEmitter.showDialog({
                title: '確認',
                errorLevel: Messages.IOPCC0003.errorLevel,
                message: Messages.IOPCC0003.toString('管理者ユーザ'),
                buttonProps: [
                    {
                        isDefault: false,
                        buttonLabel: 'キャンセル',
                        buttonHandler: this.handleRegisterCancelClick.bind(this),
                    },
                    {
                        isDefault: false,
                        buttonLabel: 'OK',
                        buttonHandler: this.handleRegisterOkClick.bind(this),
                    },
                ],
            },
        )
    }

    handleRegisterCancelClick(): void {
        this.props.eventEmitter.closeDialog()
    }

    async handleRegisterOkClick(): Promise<void> {
        this.props.eventEmitter.closeDialog()
        await this.props.eventEmitter.showLoading()
        try {

            const deleteAdminUserResponse: Nullable<NatDeleteAdminUser> = await this.props.asyncCallFunction(
                this.props.AdminUserRepo.deleteAdminUser.bind(this.props.AdminUserRepo, this.props.adminId),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                },
            )

            if (deleteAdminUserResponse && deleteAdminUserResponse.requestError === null) {
                this.props.navigate(
                    `/admin/adminUserList`,
                    {
                        state: {
                            initialMessage: {
                                title: null,
                                messageLevel: MESSAGE_TYPE.INFORMATION,
                                detail: Messages.IOPCC0009.toString('管理者ユーザ'),
                            },
                        },
                    },
                )
            }

        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    // 管理者ユーザ検索
    adminUserListLinkClick(): void {
        if (this.props.location.state && this.props.location.state.searchCondition) {
            this.props.navigate(
                `/admin/adminUserList/`,
                {state: {searchCondition: this.props.location.state.searchCondition}},
            )
        } else {
            this.props.navigate(`/admin/adminUserList/`)
        }
    }

    renderPageLinkArea(): ReactElement {
        if (this.state.dispItems === null) {
            return (<></>)
        }

        const isAsoV1FormatAdminUserId =
            this.state.roleId === ROLE_FOR_ADMINISTRATOR.ASSOC.value &&
            this.state.dispItems.loginId.length === 16 &&
            this.state.dispItems.loginId.startsWith('ASO')

        return (
            <Box mb={'15px'}>
                <PageFooterArea>
                    {!isAsoV1FormatAdminUserId &&
                        <PageFotterAreaRight>
                            <DeleteLink
                                onClick={() => this.deleteAdminUserLinkClick()}>
                                管理者ユーザを削除する
                            </DeleteLink>
                        </PageFotterAreaRight>
                    }
                    {this.state.roleId !== ROLE_FOR_ADMINISTRATOR.ASSOC.value &&
                        < PageFotterAreaLeft>
                            < AdminPrimaryButton
                                onClick={() => this.editAdminUserButtonClick(this.props.adminId)}>
                                編集する
                            </AdminPrimaryButton>
                        </PageFotterAreaLeft>
                    }
                </PageFooterArea>
            </Box>
        )
    }

    renderDetailsTable(): ReactNode {
        const {roleId, currentDateTime} = this.state

        if (this.state.dispItems === null || currentDateTime === null) {
            return null
        }

        return (
            <Table>
                <StyledTableCaption>管理者情報</StyledTableCaption>
                <TableBody>
                    <TableRow>
                        <TableRowName>権限</TableRowName>
                        <TableCell colSpan={3}>
                            {this.state.dispItems.roleName}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableRowName>物件グループ</TableRowName>
                        <TableCell colSpan={3}>
                            {this.state.dispItems.condominiumGroupName}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableRowName>物件コード</TableRowName>
                        <TableCell colSpan={3}>
                            {this.state.dispItems.condominiumCd}
                        </TableCell>
                    </TableRow>
                    {roleId === ROLE_FOR_ADMINISTRATOR.FRONT_STAFF.value &&
                        this.renderHandoverInfoRow(
                            this.state.dispItems.handoverCondominiumGroupName,
                            this.state.dispItems.endDateOfHandoverPeriod,
                            currentDateTime,
                        )
                    }
                    <TableRow>
                        <TableRowName>管理者ID</TableRowName>
                        <TableCell colSpan={3}>
                            {this.state.dispItems.loginId}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableRowName>居住者ユーザ</TableRowName>
                        <TableCell colSpan={3}>
                            {this.state.dispItems.residentUser}
                        </TableCell>
                    </TableRow>
                        <TableRow>
                            <TableRowName>メールアドレス</TableRowName>
                            <TableCell colSpan={3}>
                                {this.state.dispItems.mailAddress}
                            </TableCell>
                        </TableRow>

                    <TableRow>
                        <TableRowName>氏名</TableRowName>
                        <TableCell colSpan={3}>
                            {this.state.dispItems.name}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableRowName>任期開始日</TableRowName>
                        <TableCell>
                            {(this.state.dispItems.termStartDate) ?
                                formatDateSlashSeparator(this.state.dispItems.termStartDate) : ''}
                        </TableCell>
                        <TableRowName>任期終了日</TableRowName>
                        <TableCell>
                            {(this.state.dispItems.termEndDate) ?
                                formatDateSlashSeparator(this.state.dispItems.termEndDate) : ''}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableRowName>ログイン可否</TableRowName>
                        <TableCell colSpan={3}>
                            {this.state.dispItems.loginAvailability.label}
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        )
    }

    renderHandoverInfoRow(
        handoverCondominiumGroupName: Nullable<string>,
        endDateOfHandoverPeriod: Nullable<Moment>,
        currentDateTime: Moment,
    ): ReactNode {
        let displayHandoverSetting = ''
        if (handoverCondominiumGroupName && endDateOfHandoverPeriod) {
            displayHandoverSetting = `${handoverCondominiumGroupName}（${formatYearAndMonth(endDateOfHandoverPeriod)}末まで参照可能）`
            if (currentDateTime.isAfter(endDateOfHandoverPeriod.endOf('day'))) {
                displayHandoverSetting = displayHandoverSetting + '※期間終了済'
            }
        }

        return <TableRow>
            <TableRowName>所属変更時の引継設定</TableRowName>
            <TableCell colSpan={3}>{displayHandoverSetting}</TableCell>
        </TableRow>
    }

    renderPageFooterArea(): ReactElement {
        return (
            <PageFotterAreaLeft>
                <BackLink onClick={() => this.adminUserListLinkClick()}>
                    管理者ユーザ検索へ戻る
                </BackLink>
            </PageFotterAreaLeft>
        )
    }

    render(): ReactElement {
        if (!this.state.isLoadCompleted) {
            return (<></>)
        }
        return (
            <>
                <AuthorityChecker permittedAuthorities={HKConstants.CMN.AUTH.EDIT_ADMIN_USER}>
                    {this.renderPageLinkArea()}
                </AuthorityChecker>
                <PageMainArea>
                    {this.renderDetailsTable()}
                </PageMainArea>
                <PageFooterArea>
                    {this.renderPageFooterArea()}
                </PageFooterArea>
            </>
        )
    }

}

export default createAdminPage(NatAdminAdminUserPage)
